import Link from 'next/link';
import type { VariantProps } from 'tailwind-variants';

import { styles } from './UiKitButtonBase.styles';

type Variants = VariantProps<typeof styles>;
type NativeElement = React.ButtonHTMLAttributes<HTMLButtonElement>;
type LinkElement = React.AnchorHTMLAttributes<HTMLAnchorElement>;

export type ButtonColor = Variants['color'];

export type BaseProps = {
  as?: string;
  color?: ButtonColor;
  className?: string;
  children?: React.ReactNode;
  loading?: Variants['loading'];
  disabled?: Variants['disabled'];
  rounded?: Variants['rounded'];
  childrenClassName?: string;
};

export type ButtonProps = {
  as?: 'button';
  onClick?: NativeElement['onClick'];
  type?: NativeElement['type'];
} & React.RefAttributes<HTMLButtonElement>;

export type LinkProps = {
  as: 'link';
  onClick?: LinkElement['onClick'];
  href: string;
  target?: LinkElement['target'];
  rel?: LinkElement['rel'];
} & React.RefAttributes<HTMLAnchorElement>;

type PropsAs = ButtonProps | LinkProps;

export type UiKitButtonBaseProps = BaseProps & PropsAs;

export function UiKitButtonBase({
  className,
  children,
  color,
  loading,
  rounded,
  childrenClassName,
  ...props
}: UiKitButtonBaseProps) {
  const slots = styles({
    color,
    loading,
    disabled: props.disabled,
    rounded
  });

  const childrenContent = (
    <>
      {loading && <span className={slots.loading()} />}
      <span className={slots.children({ className: childrenClassName })}>
        {children}
      </span>
    </>
  );

  if (props.as === 'link') {
    return (
      <Link
        className={slots.base({ className })}
        {...props}
        as={undefined}
      >
        {childrenContent}
      </Link>
    );
  }

  return (
    <button
      className={slots.base({ className })}
      type="button"
      {...props}
      disabled={loading || props.disabled}
    >
      {childrenContent}
    </button>
  );
}
